"use client";

import React, { useState, useEffect } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="213841" className="hidden md:flex gap-6 items-center">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-5 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative text-foreground font-default font-medium text-sm py-2 px-1 after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-0.5 after:bg-accent-foreground after:transition-all after:duration-300 hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onLinkClick
}: {
  onLinkClick: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-8">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="h-6 w-32 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <div className="flex flex-col gap-6 mt-8">
      {menuItems.map((item, i) => <div key={item.id} data-index={i}>
          <Link to={item.href || "#"} className="text-foreground font-default font-medium text-lg block py-2 border-b border-border/50" onClick={onLinkClick}>
            {item.label}
          </Link>
        </div>)}
      <div className="mt-4">
        <Link to="#" onClick={onLinkClick}>
          <Button className="w-full bg-primary text-primary-foreground border-0 shadow-sm h-12 text-base font-default font-medium rounded-full">
            Contact Opnemen
          </Button>
        </Link>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="header" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-[#fff9f6]/85 backdrop-blur-[12px] shadow-sm border-b ${isScrolled ? "border-border/50" : "border-transparent"}`}>
      <div className="container mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
        <div className="flex-shrink-0">
          <Link to="/">
            <img src="https://trusty-molly.10web.cloud/wp-content/uploads/2026/04/cropped-2.png" alt="Connected Online" className="!w-[148px] !h-[30px]" />
          </Link>
        </div>

        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="37">
            <HeaderMenuConsumer />
          </MenuProvider>
        </div>

        <div className="hidden md:flex flex-shrink-0 items-center">
          <Link to="/contact">
            <Button className="inline-flex items-center justify-center gap-2 whitespace-nowrap [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive py-2 has-[>svg]:px-3 text-primary-foreground border-0 shadow-sm transition-all duration-300 relative overflow-hidden h-11 px-8 rounded-full font-default font-medium text-sm !bg-[hsl(250,75%,67%)] disabled:pointer-events-none disabled:opacity-50 dark:aria-invalid:ring-destructive/40 hover:bg-primary/90 hover:shadow-lg after:absolute after:inset-0 after:bg-white/10 after:opacity-0 hover:after:opacity-100 after:transition-opacity">Connectie maken</Button>
          </Link>
        </div>

        <div className="md:hidden flex items-center">
          <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground">
                <Menu className="size-6" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="pt-12">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="flex justify-between items-center mb-8">
                <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
                  <img src="https://trusty-molly.10web.cloud/wp-content/uploads/2026/04/cropped-2.png" alt="Connected Online" className="!h-[38px]" />
                </Link>
              </div>
              <MenuProvider menu_id="37">
                <MobileMenuConsumer onLinkClick={() => setIsMobileMenuOpen(false)} />
              </MenuProvider>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}